export interface CantonInfo {
  code: string;
  name: string;
  primaryColor: string;
  secondaryColor: string;
}

const CANTONS: Record<string, CantonInfo> = {
  ZH: { code: 'ZH', name: 'Zürich', primaryColor: '#0054A6', secondaryColor: '#FFFFFF' },
  BE: { code: 'BE', name: 'Bern', primaryColor: '#CE1126', secondaryColor: '#FFD700' },
  LU: { code: 'LU', name: 'Luzern', primaryColor: '#0054A6', secondaryColor: '#FFFFFF' },
  UR: { code: 'UR', name: 'Uri', primaryColor: '#FFD700', secondaryColor: '#000000' },
  SZ: { code: 'SZ', name: 'Schwyz', primaryColor: '#CE1126', secondaryColor: '#FFFFFF' },
  OW: { code: 'OW', name: 'Obwalden', primaryColor: '#CE1126', secondaryColor: '#FFFFFF' },
  NW: { code: 'NW', name: 'Nidwalden', primaryColor: '#CE1126', secondaryColor: '#FFFFFF' },
  GL: { code: 'GL', name: 'Glarus', primaryColor: '#CE1126', secondaryColor: '#000000' },
  ZG: { code: 'ZG', name: 'Zug', primaryColor: '#0054A6', secondaryColor: '#FFFFFF' },
  FR: { code: 'FR', name: 'Fribourg', primaryColor: '#000000', secondaryColor: '#FFFFFF' },
  SO: { code: 'SO', name: 'Solothurn', primaryColor: '#CE1126', secondaryColor: '#FFFFFF' },
  BS: { code: 'BS', name: 'Basel-Stadt', primaryColor: '#000000', secondaryColor: '#FFFFFF' },
  BL: { code: 'BL', name: 'Basel-Landschaft', primaryColor: '#CE1126', secondaryColor: '#FFFFFF' },
  SH: { code: 'SH', name: 'Schaffhausen', primaryColor: '#FFD700', secondaryColor: '#006847' },
  AR: { code: 'AR', name: 'Appenzell Ausserrhoden', primaryColor: '#FFFFFF', secondaryColor: '#000000' },
  AI: { code: 'AI', name: 'Appenzell Innerrhoden', primaryColor: '#000000', secondaryColor: '#FFFFFF' },
  SG: { code: 'SG', name: 'St. Gallen', primaryColor: '#006847', secondaryColor: '#FFFFFF' },
  GR: { code: 'GR', name: 'Graubünden', primaryColor: '#000000', secondaryColor: '#FFFFFF' },
  AG: { code: 'AG', name: 'Aargau', primaryColor: '#0054A6', secondaryColor: '#000000' },
  TG: { code: 'TG', name: 'Thurgau', primaryColor: '#006847', secondaryColor: '#FFFFFF' },
  TI: { code: 'TI', name: 'Ticino', primaryColor: '#CE1126', secondaryColor: '#0054A6' },
  VD: { code: 'VD', name: 'Vaud', primaryColor: '#006847', secondaryColor: '#FFFFFF' },
  VS: { code: 'VS', name: 'Valais', primaryColor: '#CE1126', secondaryColor: '#FFFFFF' },
  NE: { code: 'NE', name: 'Neuchâtel', primaryColor: '#006847', secondaryColor: '#CE1126' },
  GE: { code: 'GE', name: 'Genève', primaryColor: '#FFD700', secondaryColor: '#CE1126' },
  JU: { code: 'JU', name: 'Jura', primaryColor: '#CE1126', secondaryColor: '#FFFFFF' },
};

export function getCantonByCode(code: string): CantonInfo | undefined {
  return CANTONS[code.toUpperCase()];
}

export function getAllCantons(): CantonInfo[] {
  return Object.values(CANTONS);
}

const CANTON_NAMES: Record<string, Record<string, string>> = {
  ZH: { de: 'Zürich', fr: 'Zurich', en: 'Zurich', it: 'Zurigo' },
  BE: { de: 'Bern', fr: 'Berne', en: 'Bern', it: 'Berna' },
  LU: { de: 'Luzern', fr: 'Lucerne', en: 'Lucerne', it: 'Lucerna' },
  UR: { de: 'Uri', fr: 'Uri', en: 'Uri', it: 'Uri' },
  SZ: { de: 'Schwyz', fr: 'Schwyz', en: 'Schwyz', it: 'Svitto' },
  OW: { de: 'Obwalden', fr: 'Obwald', en: 'Obwalden', it: 'Obvaldo' },
  NW: { de: 'Nidwalden', fr: 'Nidwald', en: 'Nidwalden', it: 'Nidvaldo' },
  GL: { de: 'Glarus', fr: 'Glaris', en: 'Glarus', it: 'Glarona' },
  ZG: { de: 'Zug', fr: 'Zoug', en: 'Zug', it: 'Zugo' },
  FR: { de: 'Freiburg', fr: 'Fribourg', en: 'Fribourg', it: 'Friburgo' },
  SO: { de: 'Solothurn', fr: 'Soleure', en: 'Solothurn', it: 'Soletta' },
  BS: { de: 'Basel-Stadt', fr: 'Bâle-Ville', en: 'Basel-Stadt', it: 'Basilea Città' },
  BL: { de: 'Basel-Landschaft', fr: 'Bâle-Campagne', en: 'Basel-Landschaft', it: 'Basilea Campagna' },
  SH: { de: 'Schaffhausen', fr: 'Schaffhouse', en: 'Schaffhausen', it: 'Sciaffusa' },
  AR: { de: 'Appenzell Ausserrhoden', fr: 'Appenzell Rhodes-Extérieures', en: 'Appenzell Ausserrhoden', it: 'Appenzello Esterno' },
  AI: { de: 'Appenzell Innerrhoden', fr: 'Appenzell Rhodes-Intérieures', en: 'Appenzell Innerrhoden', it: 'Appenzello Interno' },
  SG: { de: 'St. Gallen', fr: 'Saint-Gall', en: 'St. Gallen', it: 'San Gallo' },
  GR: { de: 'Graubünden', fr: 'Grisons', en: 'Graubünden', it: 'Grigioni' },
  AG: { de: 'Aargau', fr: 'Argovie', en: 'Aargau', it: 'Argovia' },
  TG: { de: 'Thurgau', fr: 'Thurgovie', en: 'Thurgau', it: 'Turgovia' },
  TI: { de: 'Ticino', fr: 'Tessin', en: 'Ticino', it: 'Ticino' },
  VD: { de: 'Waadt', fr: 'Vaud', en: 'Vaud', it: 'Vaud' },
  VS: { de: 'Wallis', fr: 'Valais', en: 'Valais', it: 'Vallese' },
  NE: { de: 'Neuenburg', fr: 'Neuchâtel', en: 'Neuchâtel', it: 'Neuchâtel' },
  GE: { de: 'Genf', fr: 'Genève', en: 'Geneva', it: 'Ginevra' },
  JU: { de: 'Jura', fr: 'Jura', en: 'Jura', it: 'Giura' },
};

export function getCantonName(code: string, locale: string): string {
  const names = CANTON_NAMES[code.toUpperCase()];
  if (!names) return code;
  const lang = locale.substring(0, 2);
  return names[lang] ?? names['de'] ?? code;
}
